import {Component, EventEmitter, inject, Input, OnInit, Output, signal} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {defer, catchError, finalize} from 'rxjs';
import {
  ChangesType,
  SnackBarService,
  IntercomService,
  SessionService,
  PermissionsService
} from '@q9elements/ui-kit/common';
import {GenerateStoriesAiDialogComponent} from '../../generate-stories-ai-dialog.component';
import {GenerateStoriesAIData} from '../../models/generate-stories-dialog';
import {GenerateStoriesAIService} from '../../services/generate-stories-ai.service';

@Component({
  selector: 'app-assign-stories-content',
  templateUrl: './assign-stories-content.component.html',
  styleUrls: ['../../generate-stories-ai-dialog.component.scss']
})
export class AssignStoriesContentComponent implements OnInit {
  @Input() public data: GenerateStoriesAIData;
  @Input() public disableSubmit = false;

  @Output() public changeStep = new EventEmitter<number>();

  protected readonly intercomService = inject(IntercomService);
  protected readonly sessionService = inject(SessionService);
  protected readonly permissionsService = inject(PermissionsService);

  public generateStoriesAIForm: FormGroup;
  public itemType = ChangesType.STORIES;
  public isLoading = signal(false);

  constructor(
    private translateService: TranslateService,
    private generateStoriesAIService: GenerateStoriesAIService,
    private snackBarService: SnackBarService,
    public dialogRef: MatDialogRef<GenerateStoriesAiDialogComponent>
  ) {}

  ngOnInit(): void {
    this.initGenerateStoriesForm();
  }

  get customFieldsFormGroup() {
    return this.generateStoriesAIForm?.get('customFields') as FormGroup;
  }

  public onSubmit(): void {
    if (this.generateStoriesAIForm.invalid || this.isLoading() || this.disableSubmit) {
      return;
    }

    this.intercomService.fireEvent(
      'ACTION',
      this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_TO_STORIES_GENERATED,
      {
        space_id: this.sessionService.teamId(),
        actor_user_id: this.sessionService.userId(),
        account_id: this.permissionsService.team()?.domain,
        event_date: new Date().toISOString(),
        product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
      }
    );

    this.isLoading.set(true);

    defer(() => {
      const message = this.translateService.instant('GPT_STORIES.GENERATION_STARTED');

      this.snackBarService.open(message);

      return this.generateStoriesAIService.generateStoriesAI(this.data.diagramId, {
        activityMxKeys: this.data.activityMxKeys,
        completionConfig: this.generateStoriesAIForm.value
      });
    })
      .pipe(
        catchError(err => {
          this.snackBarService.error('GENERAL.ERROR');

          throw err;
        }),
        finalize(() => {
          const message = this.translateService.instant('GPT_STORIES.GENERATION_STARTED');

          this.snackBarService.open(message);
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  private initGenerateStoriesForm() {
    this.generateStoriesAIForm = new FormGroup({
      requirement: new FormControl<string>(null),
      release: new FormControl<string>(null),
      customFields: new FormGroup({})
    });
  }
}
